import {ASTEROID_EMITTER_CONFIG, ASTEROID_HEALTH, BONUS_SIZE, COMETA_EMITTER_CONFIG} from '../ClickerGame.constants';
import {ClickerGCSEngine} from '../ClickerGame.types';
import {GameObjectAdapter} from '../emitter-utils/GameObjectAdapter';
import {WorldAdapter} from '../emitter-utils/WorldAdapter';
import {Asteroid} from '../game-objects/Asteroid/Asteroid';
import {AsteroidType} from '../game-objects/Asteroid/AsteroidDataComponent';
import {Cometa} from '../game-objects/Cometa/Cometa';
import {CometaType} from '../game-objects/Cometa/CometaDataComponent';
import {DI_TOKENS} from '@/di/di.tokens';
import {MainOnboardingSteps} from '@/stores/OnboardingStore/OnboardingStore.constants';

import {injected} from 'brandi';

import {IGameScript} from '@specials/gcs-engine';
import {ParticleEmitter, SpawnShapeType} from '@specials/particle-emitter';

import {OnboardingStore} from '@/stores/OnboardingStore/OnboardingStore';
import {CallbackCollector} from '@/utils/CallbackCollector';
import {RealRandom} from '@/utils/random/RealRandom';

export class SpawnBonusesScript implements IGameScript {
  private leftParticleEmitter: ParticleEmitter;
  private rightParticleEmitter: ParticleEmitter;
  private asteroidParticleEmitter: ParticleEmitter;

  constructor(
    private readonly gcs: ClickerGCSEngine,
    private readonly random: RealRandom,
    private readonly onboardingStore: OnboardingStore,
    private readonly callbacksCollector: CallbackCollector,
    private readonly cometaFactory: () => Cometa,
    private readonly asteroidFactory: () => Asteroid,
  ) {}

  public start(): void {
    const {width, height} = this.gcs.renderer.renderer.getFrameDimensions();

    this.leftParticleEmitter = new ParticleEmitter(
      new WorldAdapter(this.gcs.world),
      this.spawnComet,
      COMETA_EMITTER_CONFIG(
        {
          x: -BONUS_SIZE,
          y: height / 2,
        },
        {
          minAngle: -20,
          maxAngle: 20,
        },
      ),
    );

    this.rightParticleEmitter = new ParticleEmitter(
      new WorldAdapter(this.gcs.world),
      this.spawnComet,
      COMETA_EMITTER_CONFIG(
        {
          x: width + BONUS_SIZE,
          y: height / 2,
        },
        {
          minAngle: 200,
          maxAngle: 160,
        },
      ),
    );

    this.asteroidParticleEmitter = new ParticleEmitter(
      new WorldAdapter(this.gcs.world),
      this.spawnAsteroid,
      ASTEROID_EMITTER_CONFIG({
        type: SpawnShapeType.Rectangle,
        x: width * 0.25,
        y: -BONUS_SIZE * 2,
        width: width * 0.75 - BONUS_SIZE,
        height: BONUS_SIZE * 2,
      }),
    );

    this.callbacksCollector.add(this.onboardingStore.subscribe(this.handleOnboardingStateChanged));
    this.handleOnboardingStateChanged();
  }

  public pause(): void {
    this.leftParticleEmitter.pauseEmit();
    this.rightParticleEmitter.pauseEmit();
    this.asteroidParticleEmitter.pauseEmit();
  }

  public resume(): void {
    this.leftParticleEmitter.startEmit();
    this.rightParticleEmitter.startEmit();
    this.asteroidParticleEmitter.startEmit();
  }

  public destroy(): void {
    this.callbacksCollector.execute();
    this.leftParticleEmitter.destroy();
    this.rightParticleEmitter.destroy();
    this.asteroidParticleEmitter.destroy();
  }

  private spawnComet = (): GameObjectAdapter => {
    const cometa = this.cometaFactory();
    cometa.dataComponent.cometaType = this.random.generateIntegerNumber(1, 4) as CometaType;
    return new GameObjectAdapter(cometa);
  };

  private spawnAsteroid = (): GameObjectAdapter => {
    const asteroid = this.asteroidFactory();
    asteroid.dataComponent.type = this.random.generateIntegerNumber(1, 2) as AsteroidType;
    asteroid.healthComponent.max = ASTEROID_HEALTH;
    asteroid.healthComponent.health = ASTEROID_HEALTH;
    return new GameObjectAdapter(asteroid);
  };

  private handleOnboardingStateChanged = (): void => {
    const onboardingStep = this.onboardingStore.getOnboardingStep();

    this.leftParticleEmitter.startEmit();
    this.rightParticleEmitter.startEmit();
    this.asteroidParticleEmitter.startEmit();

    if (onboardingStep === undefined) {
      this.leftParticleEmitter.startEmit();
      this.rightParticleEmitter.startEmit();
      this.asteroidParticleEmitter.startEmit();
    }

    if (onboardingStep === MainOnboardingSteps.Taps) {
      this.leftParticleEmitter.startEmit();
      this.rightParticleEmitter.startEmit();
      this.asteroidParticleEmitter.startEmit();
      this.callbacksCollector.execute();
    }

    // if (
    //   onboardingStep === MainOnboardingStartSteps.StartCommon ||
    //   onboardingStep === MainOnboardingStartSteps.StartWithGift
    // ) {
    //   this.isSpawnEnabled = false;
    // }
  };
}

injected(
  SpawnBonusesScript,
  DI_TOKENS.gcsEngine,
  DI_TOKENS.realRandom,
  DI_TOKENS.onboardingStore,
  DI_TOKENS.callbackCollector,
  DI_TOKENS.clickerCometa,
  DI_TOKENS.clickerAsteroid,
);
